import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  TextField,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Divider,
  Button as MuiButton,
  CircularProgress,
  InputAdornment,
} from "@mui/material";
import LoadIndicator from "devextreme-react/load-indicator";
import Form, {
  Item,
  Label,
  ButtonItem,
  ButtonOptions,
  RequiredRule,
} from "devextreme-react/form";
import "./create-service-form.scss";
import { baseAxios } from "../../utils/config.js";

function SectionHeader({ title, subtitle }) {
  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="subtitle2" fontWeight={700} sx={{ letterSpacing: 0.2 }}>
        {title}
      </Typography>
      {subtitle ? (
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.35 }}>
          {subtitle}
        </Typography>
      ) : null}
    </Box>
  );
}

function MuiServiceForm({ formData, loading, isEdit, embedded }) {
  const [pricesLocked, setPricesLocked] = useState(!!formData.current.freeService);
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [values, setValues] = useState(() => readValues(formData));

  function readValues(dataRef) {
    const d = dataRef.current;
    return {
      name: d.name ?? "",
      shortcutName: d.shortcutName ?? "",
      description: d.description ?? "",
      status: d.status ?? "",
      serviceCategory: d.serviceCategory ?? "",
      freeService: !!d.freeService,
      price1: d.price1 ?? "",
      price2: d.price2 ?? "",
      price3: d.price3 ?? "",
      price4: d.price4 ?? "",
      price5: d.price5 ?? "",
      taxesIncluded: !!d.taxesIncluded,
    };
  }

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await baseAxios.get("/categories");
        const { categories } = response.data;
        setCategoryOptions(
          categories
            .sort((x, y) => x.category_name.localeCompare(y.category_name))
            .map((category) => ({
              id: category.category_id,
              name: category.category_name,
            }))
        );
      } catch (error) {
        console.error("Failed to fetch categories", error);
      }
    };
    fetchCategories();
  }, []);

  const updateField = (field, value) => {
    formData.current[field] = value;
    setValues((current) => ({ ...current, [field]: value }));
  };

  const handleFreeServiceChange = (checked) => {
    if (checked) {
      formData.current.price1 = 0;
      formData.current.price2 = 0;
      formData.current.price3 = 0;
      formData.current.price4 = 0;
      formData.current.price5 = 0;
      formData.current.taxesIncluded = false;
      setValues((current) => ({
        ...current,
        freeService: true,
        price1: 0,
        price2: 0,
        price3: 0,
        price4: 0,
        price5: 0,
        taxesIncluded: false,
      }));
    } else {
      updateField("freeService", false);
    }
    setPricesLocked(checked);
  };

  const fieldSx = {
    "& .MuiFormLabel-asterisk": {
      color: "#c62828",
    },
  };

  const priceFields = [
    { key: "price1", label: "Level 1" },
    { key: "price2", label: "Level 2" },
    { key: "price3", label: "Level 3" },
    { key: "price4", label: "Level 4" },
    { key: "price5", label: "Level 5" },
  ];

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 3,
        "& .MuiFormLabel-asterisk": {
          color: "#c62828",
          fontWeight: 800,
        },
      }}
    >
      <Box>
        <SectionHeader
          title="Basic information"
          subtitle="Name, shortcut, and classification for this service."
        />
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))" },
            gap: 2,
          }}
        >
          <TextField
            label="Service name"
            value={values.name}
            onChange={(e) => updateField("name", e.target.value)}
            required
            fullWidth
            size="small"
            disabled={loading}
            color="primary"
            sx={{ ...fieldSx, gridColumn: "1 / -1" }}
          />
          <TextField
            label="Shortcut name"
            value={values.shortcutName}
            onChange={(e) => updateField("shortcutName", e.target.value)}
            required
            fullWidth
            size="small"
            disabled={loading}
            color="primary"
            inputProps={{ maxLength: 10 }}
            helperText="Up to 10 characters"
            sx={fieldSx}
          />
          <TextField
            select
            label="Status"
            value={values.status}
            onChange={(e) => updateField("status", e.target.value)}
            required
            fullWidth
            size="small"
            disabled={loading}
            color="primary"
            sx={fieldSx}
          >
            <MenuItem value="Active">Active</MenuItem>
            <MenuItem value="Inactive">Inactive</MenuItem>
          </TextField>
          <TextField
            label="Description"
            value={values.description}
            onChange={(e) => updateField("description", e.target.value)}
            required
            fullWidth
            size="small"
            disabled={loading}
            color="primary"
            multiline
            minRows={2}
            sx={{ ...fieldSx, gridColumn: "1 / -1" }}
          />
          <TextField
            select
            label="Category"
            value={values.serviceCategory || ""}
            onChange={(e) => {
              const next = e.target.value;
              updateField("serviceCategory", next === "" ? "" : Number(next));
            }}
            required
            fullWidth
            size="small"
            disabled={loading}
            color="primary"
            sx={{ ...fieldSx, gridColumn: "1 / -1" }}
          >
            <MenuItem value="" disabled>
              Select a category
            </MenuItem>
            {categoryOptions.map((category) => (
              <MenuItem key={category.id} value={category.id}>
                {category.name}
              </MenuItem>
            ))}
          </TextField>
        </Box>
      </Box>

      <Divider />

      <Box>
        <SectionHeader
          title="Pricing"
          subtitle="Set up to five price levels, or mark the service as free."
        />
        <FormControlLabel
          control={
            <Checkbox
              color="primary"
              checked={values.freeService}
              onChange={(e) => handleFreeServiceChange(e.target.checked)}
              disabled={loading}
            />
          }
          label={
            <Box>
              <Typography variant="body2" fontWeight={600}>
                This service has no cost
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Price levels and tax options will be disabled
              </Typography>
            </Box>
          }
          sx={{
            alignItems: "flex-start",
            m: 0,
            mb: 2,
            width: "100%",
          }}
        />
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: "repeat(2, minmax(0, 1fr))",
              md: "repeat(3, minmax(0, 1fr))",
            },
            gap: 2,
            opacity: pricesLocked ? 0.55 : 1,
            transition: "opacity 160ms ease",
          }}
        >
          {priceFields.map(({ key, label }) => (
            <TextField
              key={key}
              label={label}
              type="number"
              value={values[key]}
              onChange={(e) => {
                const raw = e.target.value;
                updateField(key, raw === "" ? "" : Number(raw));
              }}
              fullWidth
              size="small"
              disabled={loading || pricesLocked}
              color="primary"
              inputProps={{ min: 0, step: "0.01" }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">$</InputAdornment>
                ),
              }}
              sx={fieldSx}
            />
          ))}
        </Box>
        <FormControlLabel
          control={
            <Checkbox
              color="primary"
              checked={values.taxesIncluded}
              onChange={(e) => updateField("taxesIncluded", e.target.checked)}
              disabled={loading || pricesLocked}
            />
          }
          label="Taxes included in price"
          sx={{ mt: 2, ml: 0 }}
        />
      </Box>

      {!embedded ? (
        <MuiButton
          type="submit"
          variant="contained"
          color="primary"
          fullWidth
          disabled={loading}
          sx={{
            mt: 1,
            py: 1.25,
            textTransform: "none",
            fontWeight: 700,
            boxShadow: "none",
            "&:hover": { boxShadow: "none" },
          }}
        >
          {loading ? (
            <CircularProgress size={22} color="inherit" />
          ) : isEdit ? (
            "Update Service"
          ) : (
            "Create Service"
          )}
        </MuiButton>
      ) : null}
    </Box>
  );
}

function DevExtremeServiceForm({ formData, loading, isEdit }) {
  const [pricesLocked, setPricesLocked] = useState();
  const [categoryOptions, setCategoryOptions] = useState([]);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await baseAxios.get("/categories");
        const { categories } = response.data;
        const catOpt = categories
          .sort((x, y) => x.category_name.localeCompare(y.category_name))
          .map((category) => ({
            text: category.category_name,
            value: {
              id: category.category_id,
              name: category.category_name,
            },
          }));
        setCategoryOptions(catOpt);
      } catch (error) {
        console.error("Failed to fetch categories", error);
      }
    };
    fetchCategories();
  }, []);

  const handleCheckboxChanged = (e) => {
    if (e.value === true) {
      formData.current.price1 = 0;
      formData.current.price2 = 0;
      formData.current.price3 = 0;
      formData.current.price4 = 0;
      formData.current.price5 = 0;
      formData.current.taxesIncluded = false;
    }
    setPricesLocked(e.value);
  };

  const statusOptions = ["Active", "Inactive"];

  return (
    <Form formData={formData.current} disabled={loading}>
      <Item
        dataField={"name"}
        editorType={"dxTextBox"}
        editorOptions={{ stylingMode: "filled", placeholder: "Service name" }}
      >
        <Label visible={false} />
        <RequiredRule message={"Please choose a service name"} />
      </Item>
      <Item
        dataField={"shortcutName"}
        editorType={"dxTextBox"}
        editorOptions={{
          maxLength: 10,
          stylingMode: "filled",
          placeholder: "Shortcut service name",
        }}
      >
        <Label visible={false} />
        <RequiredRule message={"Please choose a shortcut service name"} />
      </Item>
      <Item
        dataField={"description"}
        editorType={"dxTextBox"}
        editorOptions={{
          stylingMode: "filled",
          placeholder: "Short description",
          height: 90,
        }}
      >
        <Label visible={false} />
        <RequiredRule message={"Please add a short description of the service"} />
      </Item>
      <Item
        dataField={"status"}
        editorType={"dxSelectBox"}
        editorOptions={{
          items: statusOptions,
          searchEnabled: false,
          placeholder: "Choose a status",
        }}
        label={{ visible: false }}
      >
        <RequiredRule message={"Please choose a status"} />
      </Item>
      <Item
        dataField={"serviceCategory"}
        editorType={"dxSelectBox"}
        editorOptions={{
          items: categoryOptions,
          valueExpr: "value.id",
          displayExpr: "text",
          searchEnabled: true,
          placeholder: "Choose a category",
        }}
        label={{ visible: false }}
      >
        <RequiredRule message={"Please choose a category"} />
      </Item>
      <Item itemType="group" colCount={2} caption="Set price levels">
        <Item
          colSpan={2}
          dataField={"freeService"}
          editorType={"dxCheckBox"}
          editorOptions={{
            onValueChanged: handleCheckboxChanged,
            text: "This service does not have a cost.",
          }}
          label={{ visible: false }}
        />
        {["price1", "price2", "price3", "price4", "price5"].map((field, i) => (
          <Item
            key={field}
            itemType="group"
            colCount={1}
            caption={`Level ${i + 1}`}
            disabled={pricesLocked}
          >
            <Item
              dataField={field}
              editorType={"dxNumberBox"}
              editorOptions={{
                placeholder: "Set a price",
                min: 0,
                format: "$ #0.##",
              }}
              label={{ visible: false }}
            >
              <RequiredRule />
            </Item>
          </Item>
        ))}
      </Item>
      <Item
        disabled={pricesLocked}
        dataField={"taxesIncluded"}
        editorType={"dxCheckBox"}
        editorOptions={{ text: "Taxes Included" }}
      >
        <Label visible={false} />
      </Item>
      <ButtonItem>
        <ButtonOptions width={"100%"} type={"default"} useSubmitBehavior={true}>
          <span className="dx-button-text">
            {loading ? (
              <LoadIndicator width={"24px"} height={"24px"} visible={true} />
            ) : isEdit ? (
              "Update Service"
            ) : (
              "Create a new Service"
            )}
          </span>
        </ButtonOptions>
      </ButtonItem>
    </Form>
  );
}

export const ServiceForm = ({ formData, loading, isEdit = false, embedded = false }) => {
  if (embedded) {
    return (
      <MuiServiceForm
        formData={formData}
        loading={loading}
        isEdit={isEdit}
        embedded={embedded}
      />
    );
  }
  return (
    <DevExtremeServiceForm formData={formData} loading={loading} isEdit={isEdit} />
  );
};
